// Number Rush (numbers): kanji numerals, prices and times — read them, hear them.
import type { Card, Pack } from '../../../../shared/pack/schema'
import { choices, learnDecks, lookup, sceneFor } from '../cards'
import { isLearned } from '../srs'
import type { ChoiceItem, CardSet } from '../types'
import { t } from '../../../strings/en'

const isNumeric = (s: string) => /^[\d:,]+$/.test(s)
/**
 * Money in the trip's currency (the region kit's `currency`, B8): ¥5,000 for JPY, ₩5,000 for KRW.
 * A trip with no currency shows the plain number.
 */
export function fmtMoney(n: number, currency?: string): string {
  if (!currency) return n.toLocaleString('en-US')
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, currencyDisplay: 'narrowSymbol', maximumFractionDigits: 0 }).format(n)
}

/** Plausible wrong answers for a heard number: ×10, ÷10, nearby hundreds, swapped digits. */
export function numberVariants(answer: string, money: boolean, currency?: string): string[] {
  if (answer.includes(':')) {
    const [h, m] = answer.split(':').map(Number) as [number, number]
    const t = (hh: number, mm: number) => `${((hh + 11) % 12) + 1}:${String(mm).padStart(2, '0')}`
    return [t(h, m === 30 ? 0 : 30), t(h + 1, m), t(h, 15), t(h + 2, m), t(h - 1, m)]
  }
  const n = Number(answer.replace(/,/g, ''))
  const raw = [n * 10, n / 10, n + 100, n - 100, n + 1000, n + Math.round(n / 2 / 100) * 100, Number(String(n).split('').reverse().join(''))]
  const ok = raw.filter((x) => Number.isInteger(x) && x > 0 && x !== n)
  const f = (x: number) => (money ? fmtMoney(x, currency) : x.toLocaleString('en-US'))
  return ok.map(f)
}

const wanted = (c: Card) => c.kind === 'reading' || c.kind === 'mcq' || (c.kind === 'listen' && isNumeric(c.answer))

export const numberRush: CardSet = {
  id: 'number-rush',
  kind: 'numbers',
  title: 'Number Rush',
  blurb: t.arcade.blurbs['number-rush']!,
  theme: { glyph: '123', jpGlyph: false, color: 'blue' },
  sessionSize: 5,
  newPerSession: 2,

  pool(pack: Pack) {
    // The kit's number decks (`learn: ["numbers"]`, B7).
    const decks = learnDecks(pack, 'numbers')
    const own = decks.flatMap((d) => d.cards.filter(wanted).map((c) => c.id))
    // Number-listening cards that live in other decks (e.g. the konbini total).
    const borrowed = pack.decks
      .filter((d) => !decks.includes(d))
      .flatMap((d) => d.cards.filter((c) => c.kind === 'listen' && isNumeric(c.answer)).map((c) => c.id))
    return [...own, ...borrowed]
  },

  eligible: () => true,


  toItem(ctx, id, rand): ChoiceItem | null {
    const hit = lookup(ctx.pack, id)
    if (!hit) return null
    const { card, deck } = hit
    const ctxPart = sceneFor(card, deck)
    if (card.kind === 'reading') {
      const others = (deck.cards ?? []).flatMap((c) => (c.kind === 'reading' && c.id !== id ? [c.meaning] : []))
      const c = choices(card.meaning, others, [], rand)
      return {
        type: 'choice',
        cardId: id,
        ...ctxPart,
        big: card.script,
        question: card.tags.includes('money') ? 'What does it mean?' : 'Which number?',
        options: c.options,
        answer: c.answer,
        reveal: { big: card.script, line: `${card.script} = ${card.reading} · ${card.meaning}`, ...(card.anecdote ? { note: card.anecdote } : {}), say: card.say ?? card.script },
        xp: card.xp,
      }
    }
    if (card.kind === 'mcq') {
      const right = card.options[card.answer]!
      const c = choices(right, card.options, [], rand, card.options.length)
      return {
        type: 'choice',
        cardId: id,
        ...ctxPart,
        text: card.prompt,
        question: '',
        options: c.options,
        answer: c.answer,
        reveal: { line: right, ...(card.anecdote ? { note: card.anecdote } : {}), ...(card.say ? { say: card.say } : {}) },
        xp: card.xp,
      }
    }
    if (card.kind === 'listen') {
      const money = card.tags.includes('money')
      const time = card.answer.includes(':')
      const currency = ctx.pack.meta.currency
      const right = money ? fmtMoney(Number(card.answer.replace(/,/g, '')), currency) : card.answer
      const c = choices(right, numberVariants(card.answer, money, currency), [], rand)
      return {
        type: 'choice',
        cardId: id,
        ...ctxPart,
        listen: card.say ?? card.answer,
        question: money ? 'How much is it?' : time ? 'What time?' : 'What number?',
        options: c.options,
        answer: c.answer,
        reveal: { line: right, ...(card.say ? { big: card.say, say: card.say } : {}), ...(card.anecdote ? { note: card.anecdote } : {}) },
        xp: card.xp,
      }
    }
    return null
  },

  progress(ctx) {
    const pool = this.pool(ctx.pack)
    return { label: 'Numbers', value: pool.filter((id) => isLearned(ctx.progress.cards[id])).length, max: pool.length }
  },
}
